/* ============================================================
   EZINK welcome gift - claim logic - assets/ez-gift.js
   ------------------------------------------------------------
   Signing up through Alia adds a free tattoo to the cart. This
   file owns the claim; ez-gift-ui.js owns how it renders. Both
   are additive: ez-cart-drawer.js is never edited.

   ALIA HOOK. This store's Alia (v3.8.33) does NOT dispatch
   alia:signup or alia:popupClose. The only alia: DOM events in
   launcher.js and app.js are loaded, debug_extension_enabled
   and user_overrides. What Alia does expose is alia.mediator,
   whose state.user carries hasEmail / email / hasPhone. A signup
   flips hasEmail false to true, so that transition is the
   signal: semantic, no page reload, fires on any popup.

   ORDER OF OPERATIONS - ADD FIRST, DISCOUNT SECOND.

   The obvious order is to apply the code, check it landed, then
   add the item. Three things killed that, all found by testing a
   genuinely cold visitor rather than a warm browser:

     1. A code restricted to one product reports applicable:false
        until that product is in the cart, so "did it apply" can
        never pass before the add.
     2. On a session with no cart yet, GET /discount/CODE often
        does not attach the code at all. It has nothing to attach
        to. This failed intermittently on a cold cart while
        working every time on a warm one.
     3. Neither the code landing nor the allocation is visible on
        the next read of /cart.js. Both are eventually consistent.

   So: add the gift, which creates the cart, THEN apply the code,
   THEN wait for the line to actually reach zero. The rollback is
   what makes that safe - if the line is still not free when the
   wait runs out it comes back out of the cart, so a returning
   customer whose once-per-customer code is refused ends up with
   an unchanged cart rather than a tattoo at full price.

   INTERRUPTED CLAIMS. A popup CTA like "Shop now" navigates, and
   a navigation kills any promise chain in flight. That can leave
   the gift added but not yet discounted. Two things cover it:
   claimGift() treats a gift that is present but NOT free as work
   to finish rather than work already done, and heal() re-checks
   on every page load. So the worst case is that the discount
   lands one page view later, and a gift that can never be free
   is cleaned up instead of sitting there at full price.
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.__EZ_GIFT__ || {};
  if (!CFG.enabled || !CFG.variantId) return;

  var VARIANT = Number(CFG.variantId);
  var CODE = String(CFG.code || 'WELCOMEGIFT');
  var FLOWS = (CFG.flowIds || []).map(Number);

  var SETTLE_TRIES = Number(CFG.settleTries || 12);   // x250ms = 3s
  var SETTLE_DELAY = Number(CFG.settleDelay || 250);

  function protId() {
    var p = window.__EZ_PROTECTION__;
    return p && p.id ? Number(p.id) : null;
  }

  function isProtLine(li) {
    var id = protId();
    return !!(id && li && Number(li.variant_id) === id);
  }

  function isGiftVariant(li) {
    return !!li && Number(li.variant_id) === VARIANT;
  }

  function giftLine(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isGiftVariant(items[i])) return items[i];
    }
    return null;
  }

  function cartHasGift(cart) { return !!giftLine(cart); }

  function giftCodeApplied(cart) {
    return ((cart && cart.discount_codes) || []).some(function (d) {
      return String(d.code).toUpperCase() === CODE.toUpperCase() && d.applicable;
    });
  }

  function giftIsFree(cart) {
    var l = giftLine(cart);
    return !!l && l.final_line_price === 0;
  }

  /* Four conditions, all required. The last two are the safety
     net: if the discount is not doing its job the line renders
     as an ordinary paid item rather than lying about the price. */
  function isGift(li, cart) {
    if (!isGiftVariant(li)) return false;
    if (!li.properties || !(li.properties._source || li.properties._gift)) return false;
    if (!giftCodeApplied(cart)) return false;
    return li.final_line_price === 0;
  }

  /* Gift-only means every REAL line is a gift. Shipping
     Protection is a service line and is ignored when deciding. */
  function isGiftOnly(cart) {
    var real = ((cart && cart.items) || []).filter(function (li) { return !isProtLine(li); });
    if (!real.length) return false;
    return real.every(isGiftVariant);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* Re-read the cart until `test` passes or we run out of tries. */
  function settle(test) {
    return new Promise(function (resolve) {
      var n = 0;
      (function attempt() {
        getCart().then(function (cart) {
          if (test(cart) || ++n >= SETTLE_TRIES) return resolve(cart);
          setTimeout(attempt, SETTLE_DELAY);
        }, function () {
          if (++n >= SETTLE_TRIES) return resolve(null);
          setTimeout(attempt, SETTLE_DELAY);
        });
      })();
    });
  }

  function warn() {
    if (window.console && console.warn) console.warn.apply(console, arguments);
  }

  /* Pull the drawer's own copy of the cart back in line with the
     server and repaint. After a cold claim the drawer was seen
     still holding the empty cart Liquid seeded at page render,
     showing the empty state over a cart that did contain the
     gift. The second pass later is cheap insurance. */
  function syncDrawer() {
    if (!window.EZCart || typeof window.EZCart.fetchCart !== 'function') return Promise.resolve();
    return window.EZCart.fetchCart().then(function () { window.EZCart.render(); });
  }

  /* ------------------------------------------------- the claim */
  var claiming = false;

  function claimGift(opts) {
    if (claiming) return Promise.resolve();
    claiming = true;
    var quiet = !!(opts && opts.quiet);   // heal() does not pop the drawer

    return getCart()
      .then(function (cart) {
        if (cartHasGift(cart)) {
          /* Already free: nothing to do. Present but NOT free: a
             previous claim was cut short, most likely by the
             popup CTA navigating. Finish it rather than treating
             it as done. */
          if (giftIsFree(cart)) return null;
          return cart;
        }

        if (!window.EZCart || typeof window.EZCart.addItems !== 'function') return null;

        return window.EZCart.addItems(
          [{ id: VARIANT, quantity: 1, properties: { _source: 'Alia signup' } }],
          { silent: true }
        ).then(getCart, getCart);
      })
      .then(function (cart) {
        if (!cart) return null;
        if (!giftLine(cart)) return null;                 // the add itself failed
        if (giftIsFree(cart)) return finish(cart, quiet);  // code was already on

        return fetch('/discount/' + encodeURIComponent(CODE), { redirect: 'follow' })
          .then(function () { return settle(giftIsFree); },
                function () { return settle(giftIsFree); })
          .then(function (c) { return finish(c, quiet); });
      })
      .catch(function (e) { warn('[ez-gift]', e); })
      .then(function () { claiming = false; }, function () { claiming = false; });
  }

  /* Keep it or take it back out. */
  function finish(cart, quiet) {
    var line = giftLine(cart);
    if (!line) return;

    if (line.final_line_price === 0) {
      return syncDrawer().then(function () {
        if (!quiet) window.EZCart.open();
        setTimeout(syncDrawer, 600);
      });
    }

    warn('[ez-gift] gift did not come out free after waiting; removing it again.');
    return window.EZCart.changeLine(line.key, 0);
  }

  /* Self-heal on every page load: if the cart is carrying a gift
     that is not free, either finish applying the discount or take
     it back out. Quiet, because the shopper did not just ask for
     anything - this is tidying up after an interrupted claim. */
  function heal() {
    getCart().then(function (cart) {
      var l = giftLine(cart);
      if (l && l.final_line_price !== 0) claimGift({ quiet: true });
    }, function () {});
  }

  /* ------------------------------------- entry 1: the email link */
  /* Strips ONLY claim-gift. Klaviyo's _kx and every utm_* stay on
     the URL: removing them breaks profile identification and
     attribution on every claim. */
  function fromUrl() {
    var u;
    try { u = new URL(window.location.href); } catch (e) { return false; }
    if (!u.searchParams.has('claim-gift')) return false;

    u.searchParams.delete('claim-gift');
    var qs = u.searchParams.toString();
    try {
      history.replaceState({}, '', u.pathname + (qs ? '?' + qs : '') + u.hash);
    } catch (e) {}

    claimGift();
    return true;
  }

  /* --------------------------------- entry 2: the Alia popup */
  function flowAllowed() {
    if (!FLOWS.length) return true;   // every Alia campaign here is a welcome popup
    try {
      var acts = window.alia.mediator.state.actions || [];
      for (var i = 0; i < acts.length; i++) {
        if (FLOWS.indexOf(Number(acts[i].flowID)) !== -1) return true;
      }
    } catch (e) {}
    return false;
  }

  function watchAlia() {
    var had = null;   // null until the first read establishes a baseline

    function identified(u) {
      return !!(u && (u.hasEmail || u.email || u.hasPhone || u.phone));
    }

    function check() {
      var u;
      try { u = window.alia.mediator.state.user; } catch (e) { return; }
      var now = identified(u);

      if (had === null) { had = now; return; }   // already signed up on arrival
      if (!had && now && flowAllowed()) { had = now; claimGift(); return; }
      had = now;
    }

    function attach() {
      var a = window.alia;
      if (!a || !a.mediator || typeof a.mediator.subscribe !== 'function') return false;
      check();
      try { a.mediator.subscribe(check); } catch (e) { return false; }

      /* Safety net: the mediator is an internal API and a future
         Alia release could stop notifying us. This poll re-reads
         the same state so a missed notification still lands. It
         stops after five minutes and does NOT survive navigation
         - heal() is what covers navigation. */
      var ticks = 0;
      var t = setInterval(function () {
        check();
        if (++ticks > 150) clearInterval(t);
      }, 2000);
      return true;
    }

    if (attach()) return;
    var tries = 0;
    var wait = setInterval(function () {
      if (attach() || ++tries > 60) clearInterval(wait);
    }, 250);
  }

  /* ------------------------------------------------------ export */
  window.EZGift = {
    config: CFG,
    variantId: VARIANT,
    code: CODE,
    claim: claimGift,
    heal: heal,
    sync: syncDrawer,
    isGift: isGift,
    isGiftVariant: isGiftVariant,
    isGiftOnly: isGiftOnly,
    isProtLine: isProtLine,
    giftCodeApplied: giftCodeApplied
  };

  function boot() {
    var claimedFromUrl = fromUrl();
    if (!claimedFromUrl) heal();
    watchAlia();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
